import {
  WORKFLOW_INBOX_PERMISSIONS,
} from '../constants/permissions';
import type { WorkflowModuleCapabilities } from '../types/workflow';
import { hasAnyPermission } from '../utils/permissions';

/**
 * Workflow capabilities from mounted backend routes (arkhrmsapi workflow.routes.js).
 *
 * Mounted and functional:
 * - GET /workflows/approvals/pending
 * - GET /workflows/:workflowInstanceId
 * - GET /workflows/:workflowInstanceId/history
 * - POST /workflows/steps/:stepId/delegate (create only; no list API)
 *
 * Mounted but intentionally non-functional (409):
 * - POST .../approve → USE_MODULE_APPROVAL_API
 * - POST .../reject → USE_MODULE_APPROVAL_API
 * - POST .../cancel → USE_MODULE_CANCEL_API
 *
 * Access: any of ATTENDANCE_APPROVE | LEAVE_APPROVE | WFH_APPROVE | PERMISSION_APPROVE
 */
export const WORKFLOW_ROUTE_CAPABILITIES = {
  inbox: true,
  details: true,
  history: true,
  approve: false,
  reject: false,
  cancel: false,
  /** Create exists; read/list does not — skip UI in this step. */
  delegationsRead: false,
  delegationsCreate: false,
} as const;

export { WORKFLOW_INBOX_PERMISSIONS };

export function getWorkflowModuleCapabilities(
  permissions: readonly string[],
): WorkflowModuleCapabilities {
  const canAccess = hasAnyPermission(permissions, WORKFLOW_INBOX_PERMISSIONS);
  return {
    canViewInbox: canAccess && WORKFLOW_ROUTE_CAPABILITIES.inbox,
    canViewDetails: canAccess && WORKFLOW_ROUTE_CAPABILITIES.details,
    canViewHistory: canAccess && WORKFLOW_ROUTE_CAPABILITIES.history,
    canApprove: canAccess && WORKFLOW_ROUTE_CAPABILITIES.approve,
    canReject: canAccess && WORKFLOW_ROUTE_CAPABILITIES.reject,
    canViewDelegations:
      canAccess && WORKFLOW_ROUTE_CAPABILITIES.delegationsRead,
    canCreateDelegation:
      canAccess && WORKFLOW_ROUTE_CAPABILITIES.delegationsCreate,
  };
}
